import { ExternalLink, Sparkles, TrendingDown, Plus, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GroupedProduct } from './groupProducts';
import { useShoppingContext } from '@/context/ShoppingContext';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import bigbasketLogo from '@/assets/stores/bigbasket.png';
import blinkitLogo from '@/assets/stores/blinkit.png';
import zeptoLogo from '@/assets/stores/zepto.png';
import instamartLogo from '@/assets/stores/instamart.png';
import flipkartLogo from '@/assets/stores/flipkart.png';
import jiomartLogo from '@/assets/stores/jiomart.png';

const storeConfig: Record<string, { name: string; logo: string }> = {
  bigbasket: { name: 'BigBasket', logo: bigbasketLogo },
  blinkit: { name: 'Blinkit', logo: blinkitLogo },
  zepto: { name: 'Zepto', logo: zeptoLogo },
  instamart: { name: 'Instamart', logo: instamartLogo },
  flipkart_minutes: { name: 'Flipkart Minutes', logo: flipkartLogo },
  jiomart: { name: 'JioMart', logo: jiomartLogo },
};

const storeFallbackSearchUrls: Record<string, string> = {
  bigbasket: 'https://www.bigbasket.com/ps/?q=',
  blinkit: 'https://blinkit.com/s/?q=',
  zepto: 'https://www.zeptonow.com/search?query=',
  instamart: 'https://www.swiggy.com/instamart/search?custom_back=true&query=',
  flipkart_minutes: 'https://www.flipkart.com/search?q=',
  jiomart: 'https://www.jiomart.com/search/',
};

interface GroupedProductCardProps {
  group: GroupedProduct;
  query: string;
  index: number;
}

export default function GroupedProductCard({ group, query, index }: GroupedProductCardProps) {
  const sortedListings = [...group.listings].sort((a, b) => a.price - b.price);
  const bestPrice = sortedListings[0]?.price ?? 0;
  const hasMultipleStores = sortedListings.length > 1;

  const { addScrapedItem } = useShoppingContext();
  const { toast } = useToast();
  const [added, setAdded] = useState(false);

  const handleAddToList = () => {
    const best = sortedListings[0];
    addScrapedItem({
      name: group.displayName,
      price: best.price,
      store: best.store,
      image: group.image,
      size: group.size,
      brand: group.brand,
      url: best.url,
    });
    setAdded(true);
    toast({ title: 'Added to list', description: `${group.displayName} added to your shopping list.` });
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div
      className="relative rounded-2xl border-2 border-border/60 bg-card overflow-hidden transition-all duration-300 hover:shadow-xl hover:border-primary/30 animate-slide-up"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Header with product info */}
      <div className="p-5 pb-3">
        <div className="flex items-start gap-4">
          {group.image ? (
            <div className="w-16 h-16 rounded-xl bg-muted/40 dark:bg-muted/60 border border-border/30 flex items-center justify-center overflow-hidden shrink-0">
              <img
                src={group.image}
                alt={group.displayName}
                className="w-14 h-14 object-contain"
                loading="lazy"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-xl bg-muted/40 dark:bg-muted/60 border border-border/30 flex items-center justify-center text-2xl shrink-0">
              🛒
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-sm text-foreground leading-tight line-clamp-2">
              {group.displayName}
            </h4>
            {group.brand && (
              <p className="text-xs text-muted-foreground mt-1 font-medium">{group.brand}</p>
            )}
            <div className="flex items-center gap-2 mt-2">
              {hasMultipleStores && (
                <Badge variant="secondary" className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-primary/10 text-primary border-primary/20">
                  <TrendingDown className="h-2.5 w-2.5 mr-1" />
                  {sortedListings.length} stores
                </Badge>
              )}
              {group.size && (
                <Badge variant="secondary" className="text-[10px] font-semibold px-2 py-0.5 rounded-lg">
                  {group.size}
                </Badge>
              )}
            </div>
            <Button
              size="sm"
              variant={added ? "secondary" : "outline"}
              onClick={handleAddToList}
              className="shrink-0 rounded-xl text-xs gap-1 h-8 mt-1"
            >
              {added ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
              {added ? 'Added' : 'Add to List'}
            </Button>
          </div>
        </div>
      </div>

      {/* Store price rows */}
      <div className="border-t border-border/40">
        {sortedListings.map((listing, idx) => {
          const cfg = storeConfig[listing.store] || { name: listing.store, logo: '' };
          const isCheapest = listing.price === bestPrice && hasMultipleStores;
          // Validate scraped URL — only use if it points to the actual store domain
          const storeDomains: Record<string, string> = {
            bigbasket: 'bigbasket.com',
            blinkit: 'blinkit.com',
            zepto: 'zeptonow.com',
            instamart: 'swiggy.com',
            flipkart_minutes: 'flipkart.com',
            jiomart: 'jiomart.com',
          };
          const isValidUrl = listing.url && listing.url.startsWith('http') && storeDomains[listing.store] && listing.url.includes(storeDomains[listing.store]);
          const link = isValidUrl ? listing.url! : (storeFallbackSearchUrls[listing.store] ? storeFallbackSearchUrls[listing.store] + encodeURIComponent(listing.name || query) : '#');

          return (
            <a
              key={`${listing.store}-${idx}`}
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-between px-5 py-3 transition-colors hover:bg-accent/50 group/row ${
                idx !== sortedListings.length - 1 ? 'border-b border-border/30' : ''
              } ${isCheapest ? 'bg-primary/5 dark:bg-primary/10' : ''}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-muted/60 dark:bg-muted border border-border/40 flex items-center justify-center overflow-hidden shrink-0">
                  {cfg.logo ? (
                    <img src={cfg.logo} alt={cfg.name} className="w-5 h-5 object-contain" loading="lazy" />
                  ) : (
                    <span className="text-xs">🏪</span>
                  )}
                </div>
                <div>
                  <span className="text-xs font-bold text-foreground">{cfg.name}</span>
                  {listing.size && listing.size !== group.size && (
                    <span className="text-[10px] text-muted-foreground ml-1.5">{listing.size}</span>
                  )}
                </div>
                {isCheapest && (
                  <Badge className="text-[9px] font-extrabold px-2 py-0 rounded-md bg-primary text-primary-foreground border-0 tracking-wide">
                    <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                    BEST
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-lg font-extrabold tracking-tight ${isCheapest ? 'text-primary' : 'text-foreground'}`}>
                  ₹{listing.price}
                </span>
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground/30 opacity-0 group-hover/row:opacity-100 group-hover/row:text-primary transition-all" />
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
